import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Box,
  Typography,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { useImpersonatableUsers } from '../../hooks/queries/useImpersonation';
import { useImpersonationStore } from '../../stores/useImpersonationStore';
import { AtlasUser, getImpersonationUserLabel } from '../../services/impersonation.service';

interface UserSelectionModalProps {
  open: boolean;
  onClose: () => void;
}

export const UserSelectionModal: React.FC<UserSelectionModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const { data: users, isLoading, error } = useImpersonatableUsers(open);
  const setImpersonatedUser = useImpersonationStore((s) => s.setImpersonatedUser);
  const hasUsers = Array.isArray(users);

  const filteredUsers = useMemo(() => {
    if (!Array.isArray(users)) return [];
    const searchLower = search.toLowerCase();
    return users.filter(
      (u) =>
        u.email.toLowerCase().includes(searchLower) ||
        u.displayName?.toLowerCase().includes(searchLower)
    );
  }, [users, search]);

  const handleSelect = (user: AtlasUser) => {
    setImpersonatedUser(user);
    onClose();
    // Reload page to apply new permissions across all queries
    window.location.reload();
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        // Prevent closing by backdrop/escape - user must select
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
      }}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      sx={{
        '& *:focus': {
          outline: 'none !important',
        },
      }}
    >
      <DialogTitle component="div">
        <Typography variant="h6">{t('impersonation.selectUser')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('impersonation.selectUserHelp')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          placeholder={t('impersonation.searchUser')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2, mt: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {!hasUsers && isLoading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {!hasUsers && !isLoading && error && (
          <Typography color="error" align="center" py={2}>
            {t('impersonation.loadingError')}
          </Typography>
        )}

        {hasUsers && (
          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {filteredUsers.length === 0 ? (
              <Typography color="text.secondary" align="center" py={2}>
                {t('impersonation.noUsers')}
              </Typography>
            ) : (
              filteredUsers.map((user) => {
                const label = getImpersonationUserLabel(user);
                return (
                  <ListItem key={user.email} disablePadding>
                    <ListItemButton onClick={() => handleSelect(user)}>
                      <ListItemText
                        primary={label}
                        secondary={label === user.email ? undefined : user.email}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })
            )}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};
